/**
 * Réglages › Assistant de recherche (add-on 24). Lecture pour tous les membres
 * qui peuvent demander ; écriture pour la direction seule. Chaque réglage part
 * seul, sous la version lue : un conflit recharge au lieu d'écraser.
 */
import { Switch, Checkbox } from '@artosera/ui';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ROLES_GALERIE } from '@artosera/shared';
import {
  useModifierReglagesRecherche,
  useReglagesRecherche,
  type ModificationReglagesRecherche,
} from '../../api/assistantRecherche.js';
import { useEtatIa } from '../../api/ia.js';
import { estApiError } from '../../api/errors.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { useShellContext } from '../shell/context.js';
import styles from './AssistantRecherchePage.module.css';

const USAGES = ['planifier', 'repondre', 'approfondir', 'lire'] as const;

export function AssistantRecherchePage(): JSX.Element {
  const { galerie, galerieSlug, compte } = useShellContext();
  if (!galerieAModule(galerie, 'assistant-recherche') || !galerieALaction(galerie, 'recherche:ask'))
    return <NotFoundPage />;
  return <ContenuReglages key={`${compte.id}:${galerieSlug}:${galerie.actions.join(',')}`} />;
}

function ContenuReglages(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { galerie, galerieSlug } = useShellContext();
  const reglages = useReglagesRecherche(galerieSlug);
  const modifier = useModifierReglagesRecherche(galerieSlug);
  const [erreur, setErreur] = useState<string | null>(null);
  const modifiable =
    galerieALaction(galerie, 'recherche:settings') &&
    galerieEcritModule(galerie, 'assistant-recherche');
  const voitIa = galerieALaction(galerie, 'ia:read');

  function enregistrer(changement: Omit<ModificationReglagesRecherche, 'version'>): void {
    if (!reglages.data) return;
    setErreur(null);
    modifier.mutate(
      { ...changement, version: reglages.data.version },
      {
        onError: (e: unknown) => {
          if (estPorteeObsolete(e)) return;
          if (estApiError(e) && e.status === 409) {
            setErreur(t('assistantRecherche.reglages.conflit'));
            void reglages.refetch();
          } else setErreur(estApiError(e) ? e.message : t('assistantRecherche.reglages.erreur'));
        },
      },
    );
  }

  const nombre = new Intl.NumberFormat(i18n.language);

  return (
    <div className={styles.page} data-ui="assistant-recherche-reglages">
      <header>
        <h1 className={styles.titre} data-ui="page-heading">
          {t('assistantRecherche.reglages.titre')}
        </h1>
        <p className={styles.sousTitre}>{t('assistantRecherche.reglages.sousTitre')}</p>
        {!modifiable ? <p>{t('assistantRecherche.reglages.lectureSeule')}</p> : null}
      </header>

      {reglages.isPending ? <p>{t('commun.chargement')}</p> : null}
      {reglages.isError ? <p role="alert">{t('assistantRecherche.reglages.erreur')}</p> : null}
      {erreur ? (
        <p role="alert" className={styles.erreur}>
          {erreur}
        </p>
      ) : null}

      {reglages.data ? (
        <>
          <section className={styles.section} aria-labelledby="ar-reglages">
            <h2 id="ar-reglages">{t('assistantRecherche.reglages.reglages')}</h2>
            <ul className={styles.liste}>
              <li>
                <Switch
                  id="ar-actif"
                  label={t('assistantRecherche.reglages.actif')}
                  checked={reglages.data.actif}
                  disabled={!modifiable}
                  pending={modifier.isPending}
                  onCheckedChange={(actif) => enregistrer({ actif })}
                />
                <p>{t('assistantRecherche.reglages.actifAide')}</p>
              </li>
              <li>
                <Switch
                  id="ar-approfondir"
                  label={t('assistantRecherche.reglages.approfondir')}
                  checked={reglages.data.approfondir}
                  disabled={!modifiable}
                  pending={modifier.isPending}
                  onCheckedChange={(approfondir) => enregistrer({ approfondir })}
                />
                <p>{t('assistantRecherche.reglages.approfondirAide')}</p>
              </li>
              <li>
                <Switch
                  id="ar-dictee"
                  label={t('assistantRecherche.reglages.dicteeWeb')}
                  checked={reglages.data.dicteeWeb}
                  disabled={!modifiable}
                  pending={modifier.isPending}
                  onCheckedChange={(dicteeWeb) => enregistrer({ dicteeWeb })}
                />
                <p className={styles.avis} role="note">
                  {t('assistantRecherche.reglages.dicteeAvis')}
                </p>
              </li>
              <li>
                <Switch
                  id="ar-documents"
                  label={t('assistantRecherche.reglages.lectureDocuments')}
                  checked={reglages.data.lectureDocuments}
                  disabled={!modifiable}
                  pending={modifier.isPending}
                  onCheckedChange={(lectureDocuments) => enregistrer({ lectureDocuments })}
                />
                <p>{t('assistantRecherche.reglages.lectureDocumentsAide')}</p>
              </li>
            </ul>
          </section>

          <section className={styles.section} aria-labelledby="ar-roles">
            <h2 id="ar-roles">{t('assistantRecherche.reglages.roles')}</h2>
            <p>{t('assistantRecherche.reglages.rolesAide')}</p>
            <ul className={styles.roles}>
              {ROLES_GALERIE.map((role) => {
                const actifs = reglages.data.roles;
                return (
                  <li key={role}>
                    <Checkbox
                      id={`ar-role-${role}`}
                      label={t(`assistantRecherche.reglages.role.${role}`)}
                      checked={actifs.includes(role)}
                      disabled={!modifiable}
                      onCheckedChange={(coche) =>
                        enregistrer({
                          roles:
                            coche === true ? [...actifs, role] : actifs.filter((r) => r !== role),
                        })
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </section>

          <section className={styles.section} aria-labelledby="ar-conso">
            <h2 id="ar-conso">{t('assistantRecherche.reglages.consommation')}</h2>
            <p>
              {t('assistantRecherche.reglages.questionsDuMois', {
                count: reglages.data.questionsDuMois,
                nombre: nombre.format(reglages.data.questionsDuMois),
              })}
            </p>
            {voitIa ? <ConsommationIa /> : null}
            {voitIa ? (
              <Link to={`/${galerieSlug}/reglages/ia`}>
                {t('assistantRecherche.reglages.voirPlafond')}
              </Link>
            ) : null}
          </section>
        </>
      ) : null}

      <section className={styles.section} aria-labelledby="ar-fournisseur">
        <h2 id="ar-fournisseur">{t('assistantRecherche.reglages.fournisseur')}</h2>
        <p>{t('assistantRecherche.reglages.fournisseurIntro')}</p>
        <dl className={styles.usages}>
          {USAGES.map((usage) => (
            <div key={usage}>
              <dt>{t(`assistantRecherche.reglages.usages.${usage}.titre`)}</dt>
              <dd>{t(`assistantRecherche.reglages.usages.${usage}.donnees`)}</dd>
            </div>
          ))}
        </dl>
        <p>{t('assistantRecherche.reglages.jamais')}</p>
      </section>
    </div>
  );
}

/** Part du plafond mensuel du socle IA, commun à tous les add-ons IA. */
function ConsommationIa(): JSX.Element | null {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const etat = useEtatIa(galerieSlug);
  if (!etat.data) return null;
  const dollars = (cents: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(
      cents / 100,
    );
  const { consommation } = etat.data;
  return (
    <p>
      {t('ia.montants', {
        depense: dollars(consommation.depenseCents),
        plafond: dollars(consommation.plafondCents),
        part: new Intl.NumberFormat(i18n.language, {
          style: 'percent',
          maximumFractionDigits: 1,
        }).format(consommation.partUtilisee),
      })}
    </p>
  );
}
